import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import EBPropTypes from 'js-utils/prop-types';
import { AU_LOCALE } from 'js-utils/intl';
import Image from './Image';
import Button from 'eventbrite_design_system/button/Button';

const FaqBanner = ({ illustration, bannerHeading, bannerCTA, onClick }) => (
    <div className="eds-g-group eds-l-pad-top-6 eds-l-pad-hor-4 eds-text--center eds-bg-color--grey-100">
        <div className="marketing-container__max-width">
            <div className="eds-g-cell eds-g-cell-1-1">
                <Image illustration={illustration} width={130} height={130} />
            </div>
            <div className="eds-g-cell eds-g-cell-1-1 eds-g-cell-mn-5-12 eds-l-mar-top-4">
                <p className="eds-text-hm eds-text-color--grey-900">
                    {bannerHeading}
                </p>
                <p className="eds-l-mar-top-8">
                    <Button style="neutral" onClick={onClick}>
                        {bannerCTA}
                    </Button>
                </p>
            </div>
        </div>
    </div>
);

const Faq = ({ question, answer, cta }) => (
    <dl className="eds-g-cell eds-g-cell-1-1 eds-g-cell-mn-1-2 eds-l-pad-hor-4 eds-l-mw-pad-hor-14 eds-l-ln-pad-hor-14 eds-l-lg-pad-hor-14 eds-l-lw-pad-hor-14 eds-l-mar-bot-4">
        <dt className="marketing-default-text eds-text-weight--heavy eds-text-bm eds-text-color--grey-900 eds-l-pad-top-2">
            {question}
        </dt>
        <dd
            className="marketing-default-text eds-l-pad-top-1"
            // Needed for HTML to render;
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
                __html: answer.toString(),
            }}
        />
        <dd
            className="marketing-default-text"
            // Needed for HTML to render;
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
                __html: cta,
            }}
        />
    </dl>
);

const _getFaqs = (faqs, pricing, showCappedFee, feeBreakdown) => {
    let faqsList = null;

    if (faqs) {
        faqsList = faqs.map(({ question, answer, cta }) => {
            let formattedAnswer = answer;

            if (typeof answer === 'function') {
                // EXPERIMENT_MARKER=LOW_ATV_CAPPED_FEE
                if (showCappedFee) {
                    const cappedFeePricing = {
                        ...pricing,
                        package1Threshold:
                            feeBreakdown.package1.formattedFeeThreshold,
                        package2Threshold:
                            feeBreakdown.package2.formattedFeeThreshold,
                        package1CappedFee:
                            feeBreakdown.package1.formattedCappedFee,
                        package2CappedFee:
                            feeBreakdown.package2.formattedCappedFee,
                    };

                    formattedAnswer = answer(
                        cappedFeePricing,
                        null,
                        showCappedFee,
                    );
                } else {
                    formattedAnswer = answer(pricing);
                }
            }

            return (
                <Faq
                    key={`${question.toString()}`}
                    question={question}
                    answer={formattedAnswer}
                    cta={cta}
                />
            );
        });
    }

    return faqsList;
};

export default class Faqs extends PureComponent {
    static propTypes = {
        /*
        Main heading for the group of FAQs
        */
        heading: EBPropTypes.translation,
        /*
        Group of faqs to show
        */
        faqs: PropTypes.arrayOf(
            PropTypes.shape({
                question: EBPropTypes.translation.isRequired,
                answer: PropTypes.oneOfType([
                    EBPropTypes.translation.isRequired,
                    PropTypes.func.isRequired,
                ]),
                cta: EBPropTypes.translation,
            }),
        ),
        /*
        Package pricing information
        */
        pricing: PropTypes.shape({
            package1: PropTypes.string.isRequired,
            package2: PropTypes.string.isRequired,
        }),
    };

    render() {
        const {
            heading,
            faqs,
            illustration,
            bannerHeading,
            bannerCTA,
            onCTAClick,
            pricing,
            showCappedFee,
            feeBreakdown,
            locale,
        } = this.props;
        const faqsList = _getFaqs(
            faqs,
            pricing.ticket || pricing,
            showCappedFee,
            feeBreakdown.ticket || feeBreakdown,
        );
        const faqBannerClassNames = classNames(
            'eds-g-cell',
            'eds-g-cell-12-12',
            'eds-l-pad-bot-16',
        );
        const headingClassNames = classNames(
            'eds-text-hm',
            'eds-text-color--grey-900',
            'eds-g-cell',
            'eds-g-cell-12-12',
            'eds-g-cell-mw-3-12',
            'eds-l-mar-bot-8',
            'eds-l-pad-left-4',
        );
        const faqListClassNames = classNames(
            'eds-g-cell',
            'eds-g-cell',
            'eds-g-cell-12-12',
            'eds-g-cell-mw-9-12',
        );
        if (!heading) {
            return null;
        }

        return (
            <div>
                <section className={faqBannerClassNames}>
                    <FaqBanner
                        bannerHeading={bannerHeading}
                        bannerCTA={bannerCTA}
                        illustration={illustration}
                        onClick={onCTAClick}
                    />
                </section>
                {locale != AU_LOCALE ? (
                    <section className="eds-bg-color--white eds-l-pad-vert-16">
                        <div className="marketing-container__max-width">
                            <h2 className={headingClassNames}>{heading}</h2>
                            <div className={faqListClassNames}>{faqsList}</div>
                        </div>
                    </section>
                ) : null}
            </div>
        );
    }
}
